import { BaseRemoteService } from "@ofqual/portal-core/core/dataAccess";
import {
  Opts,
  PagedResponse,
  QueryParams,
  ValidatedResponse,
  JSONPatchDocument,
  ModelWithMeta,
  PatchWithMeta,
} from "@ofqual/portal-core/core/types";
import { paramsToSearchString } from "@ofqual/portal-core/core/utils/urlHelper";
import { getValidationError } from "@ofqual/portal-core/core/utils/validationHelper";
import {
  fromOrgApiModel,
  toOrgApiModel,
  Scope,
} from "@ofqual/portal-organisations-scope/dist/module/domain/Scope";
import { ApproveApplication } from "../../domain/ApproveApplication";
import { CloseApplication } from "../../domain/CloseApplication";
import { Organisation } from "../../domain/Organisation";
import {
  OrganisationEngagementEvent,
  OrganisationEngagementEventUpdate,
} from "../../domain/OrganisationEngagementEvent";
import { RecognitionApplication } from "../../domain/RecognitionApplication";
import { RecognitionApplicationAttachment } from "../../domain/RecognitionApplicationAttachment";
import { RecognitionApplicationOrganisation } from "../../domain/RecognitionApplicationOrganisation";
import { RecognitionApplicationPanelAttachment } from "../../domain/RecognitionApplicationPanelAttachment";
import { RecognitionApplicationReviewAttachment } from "../../domain/RecognitionApplicationReviewAttachment";
import { RejectApplication } from "../../domain/RejectApplication";
import { ReopenApplication } from "../../domain/ReopenApplication";

export class OrgsService extends BaseRemoteService {
  public async search(
    params: QueryParams,
    opts: Opts
  ): Promise<PagedResponse<Organisation>> {
    const url = `${this.apiRoot}/organisations/${paramsToSearchString(params)}`;
    const response = await super.getRequest<PagedResponse<Organisation>>(
      url,
      opts
    );
    return {
      ...response,
      data: response.data,
    };
  }

  public async getAll(opts: Opts): Promise<Organisation[]> {
    const url = `${this.apiRoot}/organisations/${paramsToSearchString({
      pageSize: 999,
    })}`;
    const response = await super.getRequest<PagedResponse<Organisation>>(
      url,
      opts
    );
    return response.data;
  }

  public async getOrganisationByItemId(
    itemId: string,
    opts: Opts
  ): Promise<Organisation | null> {
    const url = `${
      this.apiRoot
    }/organisations/${itemId}?logResourceVisit=true`;
    const orgResponse = await super.getRequest<Organisation>(url, opts);
    const mappedOrg = await this.mapToOrgWithRecognitionApplications(
      orgResponse,
      opts
    );

    return mappedOrg;
  }

  public async patchOrganisationByItemId(
    itemId: number,
    patchDoc: PatchWithMeta<Organisation>,
    opts: Opts
  ): Promise<ValidatedResponse<Organisation>> {
    try {
      const url = `${this.apiRoot}/organisations/${itemId.toString()}`;
      const response = await super.patchRequest<
        Organisation,
        PatchWithMeta<Organisation>
      >(url, patchDoc, opts);

      return {
        isValid: true,
        data: response,
      };
    } catch (err: any) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async patchEngagementByItemId(
    itemId: number,
    patchDoc: JSONPatchDocument,
    opts: Opts
  ): Promise<ValidatedResponse<Organisation>> {
    try {
      const url = `${
        this.apiRoot
      }/organisations/${itemId.toString()}/engagement`;
      const response = await super.patchRequest<Organisation>(
        url,
        patchDoc,
        opts
      );
      const mappedOrg = await this.mapToOrgWithRecognitionApplications(
        response,
        opts
      );
      return {
        isValid: true,
        data: mappedOrg,
      };
    } catch (err: any) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async putEngagementEventByItemId(
    orgId: number,
    event: ModelWithMeta<OrganisationEngagementEvent>,
    opts: Opts
  ): Promise<OrganisationEngagementEvent> {
    const url = `${this.apiRoot}/organisations/${orgId.toString()}/event`;
    return await super.postRequest<OrganisationEngagementEvent>(
      url,
      event,
      opts
    );
  }

  public async patchEngagementEventByItemId(
    orgId: number,
    eventUpdate: ModelWithMeta<OrganisationEngagementEventUpdate>,
    opts: Opts
  ): Promise<OrganisationEngagementEventUpdate> {
    const url = `${
      this.apiRoot
    }/organisations/${orgId.toString()}/event/modify`;

    const eventModified: OrganisationEngagementEventUpdate = eventUpdate.model;
    const metaForForm = {
      model: {
        id: eventModified.referenceId,
        notes: eventModified.notes,
        wasAttended: eventModified.wasAttended,
      },
      meta: eventUpdate.meta,
    };
    const metaJson = JSON.stringify(metaForForm);

    return await super.postRequestFormData<OrganisationEngagementEventUpdate>(
      url,
      {
        files: eventModified.files,
        metaSerialised: metaJson,
      },
      opts
    );
  }

  public async downloadEngagementEventAttachment(
    id: string,
    eventId: string | null | undefined,
    blobId: string | null | undefined,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/organisations/${id}/event/${eventId}/document/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async approveChange(id: string, changeId: string, opts: Opts) {
    const url = `${this.apiRoot}/organisations/${id}/changes/${changeId}/approve`;
    return await super.postRequest(url, null, opts);
  }

  public async rejectChange(id: string, changeId: string, opts: Opts) {
    const url = `${this.apiRoot}/organisations/${id}/changes/${changeId}/reject`;
    return await super.postRequest(url, null, opts);
  }

  public async closeApplication(
    id: string,
    modelWithMeta: ModelWithMeta<CloseApplication>,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/organisations/${id}/close`;
    return await super.postRequest(url, modelWithMeta, opts);
  }

  public async reopenApplication(
    id: string,
    model: ReopenApplication,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/organisations/${id}/reopen`;
    return await super.postRequest(url, model, opts);
  }

  public async releaseApplication(id: string, opts: Opts) {
    const url = `${this.apiRoot}/organisations/${id}/release`;
    return await super.postRequest(url, null, opts);
  }

  public async sendMessage(id: string, message: string, opts: Opts) {
    const url = `${this.apiRoot}/organisations/${id}/message`;
    return await super.postRequest(url, message, opts);
  }

  public async sendAttachments(
    id: string,
    files: File[],
    note: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/organisations/${id}/documents`;
    return await super.postRequestFormData(url, { note, files }, opts);
  }

  public async downloadAttachment(
    id: string,
    activityId: string,
    blobId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/organisations/${id}/documents/${activityId}/download/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async getApplication(
    orgId: string,
    applicationId: string,
    opts: Opts
  ): Promise<RecognitionApplication | null> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}`;
    return await super.getRequest<RecognitionApplication>(url, opts);
  }

  public async getApplicationOrganistion(
    orgId: string,
    applicationId: string,
    opts: Opts
  ): Promise<RecognitionApplicationOrganisation> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/organisation`;

    return await super.getRequest<RecognitionApplicationOrganisation>(
      url,
      opts
    );
  }

  public async getApplicationAttachments(
    orgId: string,
    applicationId: string,
    opts: Opts
  ): Promise<PagedResponse<RecognitionApplicationAttachment>> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/attachments?pageSize=9999`;
    return await super.getRequest<
      PagedResponse<RecognitionApplicationAttachment>
    >(url, opts);
  }

  public async patchApplication(
    orgId: string,
    applicationId: string,
    patchDoc: PatchWithMeta<RecognitionApplication>,
    opts: Opts
  ): Promise<ValidatedResponse<RecognitionApplication>> {
    try {
      const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}`;

      const response = await super.patchRequest<
        RecognitionApplication,
        PatchWithMeta<RecognitionApplication>
      >(url, patchDoc, opts);

      return {
        isValid: true,
        data: response,
      };
    } catch (err: any) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async approveApplication(
    orgId: string,
    applicationId: string,
    approveApplication: ApproveApplication,
    opts: Opts
  ): Promise<RecognitionApplication> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/approve`;
    return await super.postRequest<RecognitionApplication>(
      url,
      approveApplication,
      opts
    );
  }

  public async rejectApplication(
    orgId: string,
    applicationId: string,
    rejectApplication: ModelWithMeta<RejectApplication>,
    opts: Opts
  ): Promise<RecognitionApplication> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/reject`;
    return await super.postRequest<RecognitionApplication>(
      url,
      rejectApplication,
      opts
    );
  }

  public async withdrawApplication(
    orgId: string,
    applicationId: string,
    opts: Opts
  ): Promise<RecognitionApplication> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/withdraw`;
    return await super.postRequest<RecognitionApplication>(url, null, opts);
  }

  public async downloadApplicationAttachment(
    orgId: string,
    applicationId: string,
    blobId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/attachments/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async getApplicationReviewAttachments(
    orgId: string,
    applicationId: string,
    opts: Opts
  ): Promise<PagedResponse<RecognitionApplicationReviewAttachment>> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/review-attachments?pageSize=9999`;

    return await super.getRequest<
      PagedResponse<RecognitionApplicationReviewAttachment>
    >(url, opts);
  }

  public async getApplicationPanelAttachments(
    orgId: string,
    applicationId: string,
    opts: Opts
  ): Promise<PagedResponse<RecognitionApplicationPanelAttachment>> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/panel-attachments?pageSize=9999`;

    return await super.getRequest<
      PagedResponse<RecognitionApplicationPanelAttachment>
    >(url, opts);
  }

  public async downloadApplicationReviewAttachment(
    orgId: string,
    applicationId: string,
    blobId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/review-attachments/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async downloadApplicationPanelAttachment(
    orgId: string,
    applicationId: string,
    blobId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/panel-attachments/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async submitReviewDocuments(
    orgId: string,
    applicationId: string,
    attachments: File[],
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/review-attachments`;
    return await super.postRequestFormData(url, { files: attachments }, opts);
  }

  public async submitPanelDocuments(
    orgId: string,
    applicationId: string,
    attachments: File[],
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/panel-attachments`;
    return await super.postRequestFormData(url, { files: attachments }, opts);
  }

  public async deletePanelDocument(
    orgId: string,
    applicationId: string,
    id: string,
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/panel-attachments/${id}`;
    return await super.deleteRequest(url, opts);
  }

  public async deleteReviewDocument(
    orgId: string,
    applicationId: string,
    id: string,
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/review-attachments/${id}`;
    return await super.deleteRequest(url, opts);
  }

  public async getApplicationScopes(
    orgId: string,
    applicationId: string,
    opts: Opts
  ): Promise<Scope[]> {
    try {
      const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/scopes`;
      const scopes = await super.getRequest<Scope[]>(url, opts);
      return scopes.map((scope) => fromOrgApiModel(scope));
    } catch (err: any) {
      if (err && err.status === 404) {
        return [];
      }
      throw err;
    }
  }
  public async postScopes(
    orgId: string,
    applicationId: string,
    scopes: Scope[],
    opts: Opts
  ) {
    try {
      const url = `${this.apiRoot}/organisations/${orgId}/recognition-applications/${applicationId}/scopes`;
      const mappedScopes = scopes.map((scope) => toOrgApiModel(scope));
      const response = await super.postRequest<Scope[]>(
        url,
        mappedScopes,
        opts
      );
      return response.map((scope) => fromOrgApiModel(scope));
    } catch (err) {
      throw err;
    }
  }

  public async postAssignOrganisation(
    orgId: string,
    assignedToUpn: string,
    assignedToDisplayName: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/organisations/${orgId}/assign`;
    const response = await super.postRequest<Organisation>(
      url,
      { assignedToUpn, assignedToDisplayName },
      opts
    );
    return response;
  }

  public async unassign(id: string, opts: Opts) {
    const url = `${this.apiRoot}/organisations/${id}/unassign`;
    return await super.postRequest(url, null, opts);
  }

  private async mapToOrgWithRecognitionApplications(
    org: Organisation,
    opts: Opts
  ) {
    const applicationUrl = `${this.apiRoot}/organisations/${org.id}/recognition-applications`;
    try {
      const applicationResponse = await super.getRequest<
        RecognitionApplication[]
      >(applicationUrl, opts);

      if (applicationResponse) {
        return {
          ...org,
          recognitionApplications: applicationResponse.sort((a, b) =>
            a.applicationNumber < b.applicationNumber ? 1 : -1
          ),
        };
      }
    } catch (err) {}

    return org;
  }
}
